import React, { useState } from 'react';
import {
  X,
  Store,
  User,
  Mail,
  Phone,
  Calendar,
  Percent,
  Save,
  CheckCircle,
  Clock,
  ShieldCheck,
  ShieldX,
} from 'lucide-react';
import { formatJalaliDate } from '@/lib/format';
import { persianDigits } from '@/features/reports/lib/format';
import { persianDisplayText } from '@/lib/persianUi';

export default function VendorDetailModal({
  isOpen,
  vendor,
  onClose,
  onVerifyToggle,
  onCommissionUpdate,
  commissionUpdating,
}) {
  const [commissionRate, setCommissionRate] = useState(
    String(vendor?.commission_rate ?? '')
  );

  if (!isOpen || !vendor) return null;

  const handleAction = async (isVerified) => {
    await onVerifyToggle(vendor.id, isVerified);
  };

  const handleCommissionSubmit = async (event) => {
    event.preventDefault();
    const parsedRate = Number(commissionRate);

    if (!Number.isFinite(parsedRate) || parsedRate < 0 || parsedRate > 100) {
      return;
    }

    await onCommissionUpdate(vendor.id, parsedRate);
  };

  const ownerName = [vendor.first_name, vendor.last_name]
    .map((name) => persianDisplayText(name, ''))
    .filter(Boolean)
    .join(' ') || '—';

  return (
    <div className="admin-modal-backdrop" onClick={onClose}>
      <div
        className="admin-modal admin-modal-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="admin-modal-header">
          <div className="admin-modal-header-left">
            <div className="admin-modal-icon neutral">
              <Store size={20} />
            </div>

            <div>
              <h3 className="admin-modal-title">
                {persianDisplayText(vendor.vendor_shop_name_fa, 'اطلاعات فروشگاه')}
              </h3>
            </div>
          </div>

          <button
            onClick={onClose}
            className="admin-modal-close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="admin-modal-body">
          <div
            className={`admin-modal-banner ${
              vendor.is_verified ? 'success' : 'pending'
            }`}
          >
            {vendor.is_verified ? (
              <CheckCircle size={18} />
            ) : (
              <Clock size={18} />
            )}

            <span>
              وضعیت احراز هویت:{' '}
              {vendor.is_verified
                ? 'تایید شده'
                : 'در انتظار بررسی و تایید'}
            </span>
          </div>

          <div className="admin-detail-grid">
            <div className="admin-detail-item">
              <div className="admin-detail-label">
                <Store size={14} />
                <span>نام فروشگاه</span>
              </div>

              <p className="admin-detail-value">
                {persianDisplayText(vendor.vendor_shop_name_fa)}
              </p>
            </div>

            <div className="admin-detail-item">
              <div className="admin-detail-label">
                <User size={14} />
                <span>نام و نام خانوادگی مالک</span>
              </div>

              <p className="admin-detail-value">
                {ownerName}
              </p>
            </div>

            <div className="admin-detail-item">
              <div className="admin-detail-label">
                <Mail size={14} />
                <span>پست الکترونیک</span>
              </div>

              <p
                className="admin-detail-value"
                style={{ fontFamily: 'monospace' }}
              >
                {vendor.email || '—'}
              </p>
            </div>

            <div className="admin-detail-item">
              <div className="admin-detail-label">
                <Phone size={14} />
                <span>شماره تماس فروشگاه</span>
              </div>

              <p
                className="admin-detail-value"
                style={{ fontFamily: 'monospace' }}
              >
                {persianDigits(vendor.vendor_local_phone) || '—'}
              </p>
            </div>

            <div className="admin-detail-item">
              <div className="admin-detail-label">
                <Phone size={14} />
                <span>شماره موبایل مالک</span>
              </div>

              <p
                className="admin-detail-value"
                style={{ fontFamily: 'monospace' }}
              >
                {persianDigits(vendor.mobile_phone) || '—'}
              </p>
            </div>

            <div className="admin-detail-item">
              <div className="admin-detail-label">
                <Calendar size={14} />
                <span>تاریخ ثبت‌نام</span>
              </div>

              <p className="admin-detail-value">
                {formatJalaliDate(vendor.created_at)}
              </p>
            </div>

            <div className="admin-detail-item">
              <div className="admin-detail-label">
                <Store size={14} />
                <span>شماره شبا</span>
              </div>

              <p
                className="admin-detail-value"
                style={{ fontFamily: 'monospace' }}
              >
                {vendor.shaba_number || '—'}
              </p>
            </div>

            <form
              className="admin-detail-item vendor-commission-form"
              onSubmit={handleCommissionSubmit}
            >
              <label
                className="admin-detail-label"
                htmlFor="vendor-commission-rate"
              >
                <Percent size={14} />
                <span>نرخ کمیسیون</span>
              </label>

              <div className="vendor-commission-control">
                <input
                  id="vendor-commission-rate"
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={commissionRate}
                  onChange={(event) => setCommissionRate(event.target.value)}
                  disabled={commissionUpdating}
                  aria-label="نرخ کمیسیون فروشنده به درصد"
                />
                <span>٪</span>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={commissionUpdating}
                >
                  <Save size={15} />
                  <span>{commissionUpdating ? 'در حال ذخیره...' : 'ذخیره'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="admin-modal-footer">
          <button
            onClick={onClose}
            className="btn btn-ghost"
          >
            بستن
          </button>

          {vendor.is_verified ? (
            <button
              onClick={() => handleAction(false)}
              className="btn btn-danger"
            >
              <ShieldX size={16} />
              <span>لغو تایید صلاحیت</span>
            </button>
          ) : (
            <button
              onClick={() => handleAction(true)}
              className="btn btn-primary"
            >
              <ShieldCheck size={16} />
              <span>تایید صلاحیت و فعال‌سازی</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
